/**
 * `mapApiError(err, opts?)` — turn any thrown error from `edenData()` (an
 * `ApiCallError`) or `authedFetch` into a presentation object the
 * notification dispatcher can render.
 *
 * Pure function. No side effects (no logging, no toast). Caller owns the
 * dispatch — usually via `useNotifyApiError()`.
 *
 * Resolution order:
 *  1. Extract `{ code, message }` via the existing `getApiError` helper.
 *  2. Look the code up in `ERROR_REGISTRY`.
 *  3. If found, return that presentation — but if it's a generic `toast`
 *     and the caller passed `fallbackMessage`, the caller's text wins over
 *     the registry default (covers context-specific copy without bloating
 *     the registry).
 *  4. If the code is in `opts.silentCodes`, return a `silent` presentation
 *     so the caller can show an inline form error.
 *  5. If unknown, return `FALLBACK_PRESENTATION` with the caller's
 *     already-localized `fallbackMessage` (or the localized registry key).
 *     Backend text is deliberately not surfaced because its language is not
 *     part of the API contract and could leak Thai into the English UI.
 *
 * In dev, an unknown code logs a `console.warn` so it gets noticed before
 * shipping.
 */
import { z } from "zod";
import { ERROR_REGISTRY, type ErrorPresentation, FALLBACK_PRESENTATION } from "@/lib/error-registry";
import { getApiError } from "@/types/api";

export interface MapApiErrorOptions {
  /**
   * Override the toast message for unknown codes AND for any toast-kind
   * registry entry. Useful for endpoint-specific copy:
   *   `notifyApiError(err, { fallbackMessage: "สร้างสินค้าไม่สำเร็จ" })`
   */
  fallbackMessage?: string;
  /**
   * Codes the caller will handle itself (inline form error, custom modal,
   * etc.) — return `silent` for these.
   */
  silentCodes?: ReadonlyArray<string>;
}

export function mapApiError(err: unknown, opts: MapApiErrorOptions = {}): ErrorPresentation {
  const apiErr = getApiError(err);
  const code = apiErr?.code;

  if (code && opts.silentCodes?.includes(code)) {
    return { kind: "silent", hint: `Caller is handling ${code} inline` };
  }

  if (code) {
    const entry = ERROR_REGISTRY[code];
    if (entry) {
      // For toast-kind entries we let `fallbackMessage` override the registry
      // default so callers can give context (e.g. "สร้างสินค้าไม่สำเร็จ"
      // vs. the generic registry copy). Modal entries keep their full config
      // because they're already tailored.
      if (entry.kind === "toast" && opts.fallbackMessage) {
        return { ...entry, message: opts.fallbackMessage };
      }
      return entry;
    }
    if (import.meta.env.DEV) {
      console.warn(`[api-error] Unknown backend code: ${code}. Add it to ERROR_REGISTRY.`, err);
    }
  }

  return {
    ...FALLBACK_PRESENTATION,
    message: opts.fallbackMessage ?? FALLBACK_PRESENTATION.message,
  };
}

// Parses the `error.data` payload out of an `{ error: { code, message, data } }`
// envelope (an ApiCallError thrown by `edenData`, or a raw JSON error body).
const ErrorDataEnvelope = z.object({
  error: z.object({ data: z.record(z.string(), z.unknown()).optional() }).optional(),
});

/**
 * Pull the `error.data` payload from an error envelope, e.g. the structured
 * context a code like `TOKEN_INVALID` carries (platform, channelId). Returns
 * `null` when there's no object `data` payload.
 */
export function extractErrorData(err: unknown): Record<string, unknown> | null {
  return ErrorDataEnvelope.safeParse(err).data?.error?.data ?? null;
}
